import React from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage, useIntl } from '@divvy-web/i18n'
import Avatar from '@divvy-web/skylab.avatar'
import BasicButton, {
  BASIC_BUTTON_COLOR_NEUTRAL,
  BASIC_BUTTON_SIZE_MEDIUM,
  BASIC_BUTTON_TYPE_FLAT,
} from '@divvy-web/skylab.basicbutton'
import { TOAST_TYPE_BASIC, TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import type { Collaborator as CollaboratorData } from '@sputnik/hooks/useApplicationCollaborators'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useRemoveInvitedUsers } from './RemoveInvitedUser.gql'
import styles from './styles.module.scss'

export interface CollaboratorProps extends CollaboratorData {
  appId: string
  isRemoveDisabled?: boolean
  onDeleteCollaborator?: () => void
}

const Collaborator = ({
  appId,
  email,
  firstName,
  lastName,
  role,
  isRemoveDisabled,
  onDeleteCollaborator,
}: CollaboratorProps) => {
  const [getClassName] = useNameFormatter('Collaborator')
  const showToast = useToast()
  const { formatMessage } = useIntl()

  const [removeInvitedUser, { loading }] = useRemoveInvitedUsers({
    refetchQueries: ['GetApplicationEmails'],
    onCompleted: () => {
      logInfo('removeCollaborator', {
        attributes: {
          creditAppId: appId,
          result: 'collaborator removed',
        },
        eventName: 'removeCollaboratorSuccess',
      })
      onDeleteCollaborator?.()

      showToast(
        TOAST_TYPE_BASIC,
        <FormattedMessage
          defaultMessage='Collaborator removed'
          id='sputnik.Collaborator__avltpZ'
        />,
        { autoHideDelay: 3000 },
      )
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'removeCollaborator',
          creditAppId: appId,
          message: error?.message,
          result: 'failed to remove collaborator',
        },
        eventName: 'removeCollaboratorFailure',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to remove collaborator'
          id='sputnik.Collaborator__wMhKK7'
        />,
      )
    },
  })

  const avatarImageText = firstName && lastName ? `${firstName?.charAt(0)} ${lastName?.charAt(0)}` : email.charAt(0)

  const avatarText = firstName && lastName ? `${firstName} ${lastName}` : email

  const onDelete = async () => {
    await removeInvitedUser({
      variables: {
        creditApplicationId: appId,
        emails: [email],
      },
    })
  }

  return (
    <div className={styles[getClassName('')]}>
      <Avatar fallbackText={avatarImageText}>{avatarText}</Avatar>
      {!role && !isRemoveDisabled && (
        <BasicButton
          color={BASIC_BUTTON_COLOR_NEUTRAL}
          disabled={loading}
          icon='close'
          name={formatMessage(
            {
              defaultMessage: 'Remove collaborator {email}',
              id: 'sputnik.Collaborator__fecPKF',
            },
            { email },
          )}
          size={BASIC_BUTTON_SIZE_MEDIUM}
          type={BASIC_BUTTON_TYPE_FLAT}
          onClick={onDelete}
        />
      )}
    </div>
  )
}

export default Collaborator
